import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
    type Modifier,
} from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import type { PendingUpload } from "~/shared/store/types";
import { cn } from "~/shared/lib/utils";
import { MediaLightbox } from "~/shared/ui/media-lightbox";

import { AttachmentThumbBody, AttachmentThumbCaption } from "./AttachmentThumb";
import { attachmentThumbFrame } from "./attachment-thumb-frame";
import { usePendingLightbox } from "./usePendingLightbox";

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({
    ...transform,
    y: 0,
});

interface AttachmentPreviewProps {
    items: readonly PendingUpload[];
    onRemove: (localId: string) => void;
    onReorder: (fromIdx: number, toIdx: number) => void;
}

interface ThumbnailProps {
    item: PendingUpload;
    onRemove: (localId: string) => void;
    onZoom: (localId: string) => void;
}

function Thumbnail({ item, onRemove, onZoom }: ThumbnailProps) {
    const { t } = useTranslation("chat");
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.localId,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const isError = item.status === "error";

    return (
        // Подпись едет вместе с плиткой: она часть перетаскиваемого элемента, а
        // не соседняя колонка, иначе имя оставалось бы на прежнем месте очереди.
        <div
            ref={setNodeRef}
            style={style}
            className="flex w-16 shrink-0 flex-col gap-1"
            {...attributes}
            {...listeners}
        >
            <div
                className={cn(attachmentThumbFrame(isError), "aspect-square h-16 w-16")}
                title={isError ? (item.error ?? item.fileName) : item.fileName}
            >
                <AttachmentThumbBody
                    item={item}
                    onRemove={onRemove}
                    removeLabel={t("prompt.attachmentRemove")}
                    playLabel={t("prompt.attachmentPlay")}
                    pauseLabel={t("prompt.attachmentPause")}
                    onZoom={onZoom}
                    zoomLabel={t("prompt.attachmentZoom")}
                />
            </div>
            <AttachmentThumbCaption item={item} />
        </div>
    );
}

export function AttachmentPreview({ items, onRemove, onReorder }: AttachmentPreviewProps) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    const containerRef = useRef<HTMLDivElement>(null);
    const lightbox = usePendingLightbox(items);

    const restrictToContainer = useMemo<Modifier>(
        () =>
            ({ transform, draggingNodeRect }) => {
                const containerRect = containerRef.current?.getBoundingClientRect();
                if (!containerRect || !draggingNodeRect) return transform;
                const next = { ...transform };
                if (draggingNodeRect.left + transform.x < containerRect.left) {
                    next.x = containerRect.left - draggingNodeRect.left;
                } else if (draggingNodeRect.right + transform.x > containerRect.right) {
                    next.x = containerRect.right - draggingNodeRect.right;
                }
                return next;
            },
        [],
    );

    if (items.length === 0) return null;

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const fromIdx = items.findIndex((i) => i.localId === active.id);
        const toIdx = items.findIndex((i) => i.localId === over.id);
        if (fromIdx < 0 || toIdx < 0) return;
        onReorder(fromIdx, toIdx);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis, restrictToContainer]}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={items.map((i) => i.localId)} strategy={horizontalListSortingStrategy}>
                <div ref={containerRef} className="flex w-fit gap-2 px-1 py-2">
                    {items.map((item) => (
                        <Thumbnail
                            key={item.localId}
                            item={item}
                            onRemove={onRemove}
                            onZoom={lightbox.openAt}
                        />
                    ))}
                </div>
            </SortableContext>

            <MediaLightbox
                items={lightbox.items}
                initialIndex={lightbox.initialIndex}
                isOpen={lightbox.isOpen}
                onClose={lightbox.close}
            />
        </DndContext>
    );
}
